import React, { useEffect, useMemo } from 'react';
import { COLORS, FlexView, Row } from '@cuemath/comps';
import { Text } from '@cuemath/web-comps';

import { BorderedBox, CardView, HorizontalLine, FadeIn } from '../styled-comps';
import CollapsibleBox from 'comps/collapsible-box';
import styled from 'styled-components';
import { convertEpochToDateFormat, formatTimestamp, getTimeSinceEpoch } from 'utils';
import { useParams } from 'react-router-dom';
import { trackEvent } from 'modules/analytics';
import { EVENT_TYPE } from 'constants/logger';
import { ReactComponent as RedirectIcon } from 'img/redirect.svg';
import { useStudentLeavesHistoryGet } from 'actions/studentsv2';
import { isSameDate } from 'utils';
import { getFormattedDate } from 'utils';

const Wrapper = styled.div`
  animation: ${FadeIn} 0.5s ease-in-out;
`;

const ListTittle = styled(Text)`
  margin-right: 16px;
`;

const TopBarContainer = styled(Row)`
  width: 100%;
`;

const FlexViewWrapper = styled(FlexView)`
  width: 50%;
  margin-top: 16px;
`;

const ContentWrapper = styled(FlexView)`
  margin-top: 16px;
  flex-wrap: wrap;
  flex: 1;
`;

const CollapsibleBoxWrapper = styled(Row)`
  margin-top: 32px;
  align-items: flex-start;
`;

const TopBarWrapper = styled(Row)`
  margin-bottom: 16px;
`;

const InfoWrapper = styled(Row)`
  width: 50%;
`;

const FlexViewCollapsibleBoxContainer = styled(FlexView)`
  flex: 1;
  margin-left: 24px;
`;

const ImageButton = styled(RedirectIcon)`
  cursor: pointer;
  width: 16px;
  height: 16px;
  margin-left: 4px;
`;

const ComponentWrapper = styled(FlexView)`
  position: relative;
`;

const PausedText = styled(Text)`
  position: absolute;
  top: 0;
  left: 0;
  padding: 4px;
  border-radius: 2px;
  background: ${({ studentTransfer }) => (studentTransfer ? COLORS.SUN : COLORS.COLA)};
  height: 24px;
`;

const getSchoolDetailsText = (schoolName, board) => {
  if (schoolName && board) {
    return `${schoolName},\n ${board}`;
  }

  if (schoolName) {
    return schoolName;
  }

  if (board) {
    return board;
  }

  return '-';
};
const COURSE_STATES = ['ACTIVE', 'INACTIVE'];

export default function BasicDetails({ studentDetail, userId, studentId }) {
  const { id } = useParams();
  const {
    data: leaveHistoryData,
    get: getStudentLeavesHistory,
  } = useStudentLeavesHistoryGet(id);

  useEffect(() => {
    if (id) {
      getStudentLeavesHistory(id);
    }
  }, [id]);

  const {
    app_plus_feature_usage_data: appPlusFeatureUsageData,
    last_completed_class: lastCompletedClass,
    latest_paid_spl_details: latestPaidSplDetails,
    next_scheduled_class: nextScheduledClass,
    primary_course: primaryCourse,
    school_details: schoolDetails,
    upcoming_ptm_details: upcomingPtmDetails,
  } = studentDetail || {};
  const {
    last_used_on: lastUsedAppOn,
    school_exam_help: schoolExamHelp,
    school_homework_help: schoolHomeworkHelp,
  } = appPlusFeatureUsageData || {};
  const { last_used_on: lastUsedExamHelpOn } = schoolExamHelp || {};
  const { last_used_on: lastUsedHomeworkHelpOn } = schoolHomeworkHelp || {};
  const lastCompletedClassDetails = lastCompletedClass && lastCompletedClass[0];
  const nextScheduledClassDetails = nextScheduledClass && nextScheduledClass[0];
  const { class_start_ts: lastCompletedClassStartTs } = lastCompletedClassDetails || {};
  const { class_start_ts: nextScheduledClassStartTs, tag: classTag } =
    nextScheduledClassDetails || {};
  const {
    classes_balance: classBalance,
    classes_purchased: classPurchased,
    validity_till_date: validityTillDate,
    state: courseState,
    is_sdt_in_progress: isSDTInProgress, //* SDT: Studnet Direct Transfer
  } = primaryCourse || {};
  const { courses, subscription_id: subscriptionId } = latestPaidSplDetails || {};
  const { payment_subscription_details: paymentSubscriptionDetails } =
    latestPaidSplDetails || {};
  const {
    total_classes: totalClasses,
    start_dt: subscriptionPurchasedOn,
    total_duration: totalDuration,
    next_payment_dt: nextSubscriptionDT,
    admin_url: adminRedirectUrl,
  } = paymentSubscriptionDetails || {};
  const { paid_on: paidOn } =
    subscriptionPurchasedOn !== null && subscriptionPurchasedOn !== undefined
      ? { paid_on: subscriptionPurchasedOn }
      : (courses && courses[0]) || {};
  const { no_of_classes: noOfClasses } =
    totalClasses !== null && totalClasses !== undefined
      ? { no_of_classes: totalClasses }
      : latestPaidSplDetails || {};
  const { due_date: dueDate } = upcomingPtmDetails || {};
  const { board, school_name: schoolName } = schoolDetails || {};
  const { is_on_leave: isOnLeave, next_or_current_leave: nextOrCurrentLeave } =
    leaveHistoryData ?? {};
  const { start_datetime: pauseStartTS, end_datetime: pauseEndTS } =
    nextOrCurrentLeave ?? {};
  const formattedValidityTillDate =
    validityTillDate && convertEpochToDateFormat(validityTillDate * 1000);
  const formattedPaidDate = paidOn && convertEpochToDateFormat(paidOn * 1000);
  const formattedNextSubscriptionDate =
    nextSubscriptionDT && convertEpochToDateFormat(nextSubscriptionDT * 1000);
  const formattedNextScheduledClassDate =
    nextScheduledClassStartTs &&
    convertEpochToDateFormat(nextScheduledClassStartTs * 1000);
  const formattedNextScheduledClassStartTs =
    nextScheduledClassStartTs && formatTimestamp(nextScheduledClassStartTs);
  const formattedUpcomingPtmDueDate = dueDate && convertEpochToDateFormat(dueDate * 1000);
  const formattedLastCompletedClassStartDate =
    lastCompletedClassStartTs &&
    convertEpochToDateFormat(lastCompletedClassStartTs * 1000);
  const formattedLastCompletedClassStartTs =
    lastCompletedClassStartTs && formatTimestamp(lastCompletedClassStartTs);
  const classBalanceText =
    classPurchased >= 0 && classBalance >= 0 ? `${classBalance}/${classPurchased}` : '-';
  const topContainerList = [
    {
      name: 'class balance',
      statement: classBalanceText,
    },
    {
      name: 'tenure end',
      statement: formattedValidityTillDate || '-',
    },
    {
      name: 'next ptm due',
      statement: formattedUpcomingPtmDueDate || '-',
    },
  ];

  useEffect(() => {
    trackEvent(EVENT_TYPE.BASIC_DETAILS_SCREEN_VIEW, {
      student_id: studentId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const redirectPaymentAdmin = () => {
    if (adminRedirectUrl) {
      window.open(adminRedirectUrl);
    }
  };

  const pausedDates = useMemo(
    () =>
      isSameDate(pauseStartTS, pauseEndTS)
        ? getFormattedDate(pauseStartTS * 1000, 'DD MMM YY')
        : `${getFormattedDate(pauseStartTS * 1000, 'DD MMM YY')} - ${getFormattedDate(
            pauseEndTS * 1000,
            'DD MMM YY',
          )}`,
    [pauseStartTS, pauseEndTS],
  );

  return (
    <Wrapper>
      <ComponentWrapper>
        <BorderedBox padding="16px">
          {!COURSE_STATES.includes(courseState) && courseState && (
            <PausedText variant="eyebrow2" color={COLORS.WHITE}>
              course {courseState}
            </PausedText>
          )}
          {isSDTInProgress && (
            <PausedText
              studentTransfer
              color={COLORS.COLA}
              variant="eyebrow2"
              weight="700"
            >
              student Transfer is in progress
            </PausedText>
          )}
          {isOnLeave && (
            <PausedText color={COLORS.WHITE} variant="eyebrow2" weight="700">
              student is on pause
            </PausedText>
          )}

          <TopBarContainer justifyContent="space-between">
            {topContainerList?.map(({ name, statement }) => (
              <CardView justifyContent="center" alignItems="center" key={name+statement}>
                <Text variant="eyebrow2" color={COLORS.COLA_LIGHTER}>
                  {name}
                </Text>
                <Text variant="h5">{statement}</Text>
              </CardView>
            ))}
          </TopBarContainer>
          <HorizontalLine marginTop="8px" marginBottom="0" />
          <TopBarContainer>
            <FlexViewWrapper justifyContent="space-between">
              <TopBarWrapper>
                <ListTittle variant="body3">Last class</ListTittle>
                {formattedLastCompletedClassStartDate &&
                formattedLastCompletedClassStartTs ? (
                  <Text variant="body2">
                    {formattedLastCompletedClassStartDate},{' '}
                    {formattedLastCompletedClassStartTs}
                  </Text>
                ) : (
                  <Text variant="body2">-</Text>
                )}
              </TopBarWrapper>
              <Row>
                <ListTittle variant="body3">Next class</ListTittle>
                {formattedNextScheduledClassDate && formattedNextScheduledClassStartTs ? (
                  <Text variant="body2">
                    {formattedNextScheduledClassDate},{' '}
                    {formattedNextScheduledClassStartTs}{' '}
                    {classTag && `(${classTag.toUpperCase()})`}
                  </Text>
                ) : (
                  <Text variant="body2">-</Text>
                )}
              </Row>
            </FlexViewWrapper>
            <FlexViewWrapper>
              <TopBarWrapper>
                <ListTittle variant="body3">Latest plan</ListTittle>
                {noOfClasses && formattedPaidDate ? (
                  <Text variant="body2">
                    {noOfClasses} classes purchased on {formattedPaidDate}{' '}
                    {totalDuration !== null && totalDuration !== undefined
                      ? `for ${totalDuration} months`
                      : ''}
                  </Text>
                ) : (
                  <Text variant="body2">-</Text>
                )}
              </TopBarWrapper>
              {subscriptionId && paymentSubscriptionDetails && (
                <InfoWrapper>
                  <ListTittle variant="body3">Subscription Due On</ListTittle>
                  {formattedNextSubscriptionDate ? (
                    <>
                      <Text variant="body2">{formattedNextSubscriptionDate} </Text>
                      <ImageButton onClick={redirectPaymentAdmin} />
                    </>
                  ) : (
                    <Text variant="body2">-</Text>
                  )}
                </InfoWrapper>
              )}
            </FlexViewWrapper>
            <ContentWrapper flexDirection="row" justifyContent="space-between">
              <InfoWrapper>
                <ListTittle variant="body3">{`${
                  isOnLeave ? `Current` : `Next`
                } pause on`}</ListTittle>
                <Text variant="body2">{pauseStartTS ? pausedDates : '-'}</Text>
              </InfoWrapper>
            </ContentWrapper>
          </TopBarContainer>
        </BorderedBox>
      </ComponentWrapper>
      <CollapsibleBoxWrapper>
        <CollapsibleBox
          title={'APP + feature usage'}
          options={[
            {
              content: lastUsedAppOn ? getTimeSinceEpoch(lastUsedAppOn) : '-',
              title: 'Cuemath app',
              isCollapsible: false,
            },
            {
              title: 'School HW help',
              content: lastUsedHomeworkHelpOn
                ? getTimeSinceEpoch(lastUsedHomeworkHelpOn)
                : '-',
              isCollapsible: false,
            },
            {
              title: 'School exam prep',
              content: lastUsedExamHelpOn ? getTimeSinceEpoch(lastUsedExamHelpOn) : '-',
              isCollapsible: false,
            },
          ]}
        />
        <FlexViewCollapsibleBoxContainer>
          <CollapsibleBox
            title={'School related'}
            options={[
              {
                title: 'School details',
                content: getSchoolDetailsText(schoolName, board),
                isCollapsible: true,
                userId,
              },
            ]}
          />
        </FlexViewCollapsibleBoxContainer>
      </CollapsibleBoxWrapper>
    </Wrapper>
  );
}
